import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Glasses, Images, Lock, Play, Star, Leaf, Sprout } from "lucide-react";
import { api, playState } from "../../lib/api";
import { ANIMAL_FR, ITEM_FR } from "../../lib/labels";
import { useCatalog } from "../../hooks/useData";
import { PhoneFrame } from "../../components/game/PhoneFrame";
import { GameHud } from "../../components/game/GameHud";
import { GreatBaobab } from "../../components/game/GreatBaobab";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "../../components/ui/dialog";

const PLAYABLE = ["village", "savanna", "river", "forest", "bridge"];
const isOpen = (gp, key) => !!gp && (gp.unlocked_levels.includes(key) || gp.completed_levels.includes(key));
const NODES = [[28, 93], [70, 80], [32, 67], [70, 54], [30, 41], [68, 28], [32, 15], [62, 3]];
const COLORS = ["#E8C990", "#D7B56A", "#7CC4E0", "#3E9F6E", "#A47A50", "#E08A4A", "#6B7A90", "#17A2A2"];

export default function WorldMap() {
  const nav = useNavigate();
  const { sessionId, patientId } = playState.get();
  const { data: catalog } = useCatalog();
  const [gp, setGp] = useState(null);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (!sessionId) return nav("/patient");
    api.get(`/sessions/${sessionId}`).then((r) => { if (!r.data.plan) nav("/patient/calibration"); if (["completed", "stopped"].includes(r.data.status)) nav("/patient"); });
    api.get(`/patients/${patientId}/game-progress`).then((r) => setGp(r.data));
  }, [sessionId, patientId, nav]);

  const levels = catalog?.levels || [];
  const path = NODES.map(([x, y], i) => `${i ? "L" : "M"}${x} ${y}`).join(" ");
  const open = (lv) => ((lv.playable || PLAYABLE.includes(lv.key)) && isOpen(gp, lv.key) ? nav(`/patient/${lv.key}`) : setPreview(lv));

  return (
    <PhoneFrame>
      <GameHud onStop={() => nav("/patient/end", { state: { stopped: true } })} seeds={gp?.seeds ?? 0} />
      <div className="absolute inset-0 bg-gradient-to-b from-[#9ED9C9] via-[#E9DDB5] to-[#E7CB93]" data-testid="world-map">
        <button data-testid="world-map-vr" onClick={() => nav("/patient/vr")} className="absolute right-3 top-[108px] z-10 flex h-11 items-center gap-2 rounded-full bg-[#0A2540] px-4 text-xs font-extrabold text-[#FFE29A] shadow-lg"><Glasses className="h-4 w-4" />Village en VR</button>
        <button data-testid="world-map-album" onClick={() => nav("/patient/album")} className="absolute left-3 top-[108px] z-10 flex h-11 items-center gap-2 rounded-full bg-white/90 px-4 text-xs font-extrabold text-[#0A2540] shadow-lg"><Images className="h-4 w-4" />Mon album</button>
        <div className="absolute inset-x-0 bottom-[150px] top-[150px]">
        <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 h-full w-full">
          <path d={path} stroke="#fff" strokeOpacity=".8" strokeWidth="2.4" strokeDasharray="1.5 2" fill="none" vectorEffect="non-scaling-stroke" style={{ strokeWidth: 6 }} />
        </svg>
        {levels.map((lv, i) => {
          const unlocked = isOpen(gp, lv.key);
          const [x, y] = NODES[i];
          return (
            <button key={lv.key} data-testid={`world-map-level-${lv.order}`} onClick={() => open(lv)}
              className="absolute flex -translate-x-1/2 -translate-y-1/2 flex-col items-center gap-1" style={{ left: `${x}%`, top: `${y}%` }}>
              <span className={`relative flex h-16 w-16 items-center justify-center rounded-full border-4 border-white shadow-lg ${unlocked ? "" : "grayscale"}`} style={{ background: COLORS[i] }}>
                {unlocked && lv.playable && <span className="ring-wave absolute inset-0 rounded-full border-4 border-white" />}
                {unlocked ? (lv.playable ? <Play className="h-7 w-7 fill-white text-white" /> : <span className="font-display text-xl font-extrabold text-white">{lv.order}</span>) : <Lock className="h-6 w-6 text-white" />}
              </span>
              <span className={`rounded-full px-2.5 py-1 text-xs font-bold shadow ${unlocked ? "bg-[#0A2540] text-white" : "bg-white/80 text-slate-500"}`}>{lv.order}. {lv.name_fr}</span>
            </button>
          );
        })}
        </div>
        <div className="absolute inset-x-3 bottom-[max(0.75rem,env(safe-area-inset-bottom))] rounded-3xl bg-white/90 p-4 shadow-xl backdrop-blur" data-testid="rewards-panel">
          <p className="mb-2 text-center text-[10px] font-bold tracking-[0.3em] text-[#0F7C7C]" data-testid="map-title">LA QUÊTE DES GRAINES DE VIE</p>
          <div className="flex items-center justify-around text-center">
            <div className="-my-2 flex flex-col items-center" data-testid="map-great-baobab"><GreatBaobab seeds={gp?.seeds ?? 0} size={78} testId="great-baobab-map" /><p className="text-[10px] font-bold text-[#0F7C7C]">Grand Baobab</p></div>
            {[[Sprout, gp?.seeds, "Graines"], [Leaf, gp?.leaves, "Feuilles"], [Star, gp?.stars, "Étoiles"]].map(([I, v, l]) => (
              <div key={l}><I className="mx-auto h-5 w-5 text-[#17A2A2]" /><p className="font-display text-xl font-extrabold">{v ?? 0}</p><p className="text-[11px] text-slate-500">{l}</p></div>
            ))}
          </div>
          {(gp?.items.length > 0 || gp?.animals.length > 0) && (
            <p className="mt-2 text-center text-xs text-slate-600">{[...gp.items.map((k) => ITEM_FR[k]), ...gp.animals.map((k) => `${ANIMAL_FR[k]} (animal)`)].join(" · ")}</p>
          )}
        </div>
      </div>
      <Dialog open={!!preview} onOpenChange={() => setPreview(null)}>
        <DialogContent className="max-w-sm rounded-3xl" data-testid="level-preview-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">Niveau {preview?.order} · {preview?.name_fr}</DialogTitle>
            <DialogDescription className="text-base">
              {isOpen(gp, preview?.key) ? "Débloqué ! Ce niveau sera jouable dans une prochaine version." : "Verrouillé — termine les niveaux précédents pour récolter les Graines de Vie."}
            </DialogDescription>
          </DialogHeader>
          {preview && <img decoding="async" src={`/assets/level${preview.order}.webp`} alt={preview.name_fr} className="h-36 w-full rounded-2xl object-cover" />}
          <p className="rounded-2xl bg-[#EAF7F5] p-3 text-sm"><b>Objectifs thérapeutiques :</b> {preview?.objectives_fr}</p>
          <p className="text-xs font-bold tracking-widest text-slate-400">APERÇU · BIENTÔT DISPONIBLE</p>
        </DialogContent>
      </Dialog>
    </PhoneFrame>
  );
}
